import { useCallback, useEffect, useState } from "react";
import { Check, TriangleAlert } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { FormField, textareaClass } from "../components/FormField";
import { PatientCodingSummary } from "../components/PatientCodingSummary";
import { DeadlineBadge } from "../components/DeadlineBadge";
import {
  decideControl,
  markPaymentNoteSent,
  fetchControlDetail,
  fetchControlQueue,
  type ClaimsStatus,
  type ControlCheck,
  type ControlDetail,
  type ControlQueueItem,
} from "../api/claimsApi";
import { openPaymentNote, openRefactionSheet } from "../api/invoicePrintApi";

const TABS: Array<{ value: ClaimsStatus; label: string }> = [
  { value: "pending", label: "À contrôler" },
  { value: "anomaly", label: "Anomalies" },
  { value: "controlled", label: "Validées" },
];

const CHECK_VARIANT: Record<ControlCheck["status"], "success" | "warning" | "danger" | "info"> = {
  ok: "success",
  warn: "warning",
  fail: "danger",
  na: "info",
};

const CHECK_LABEL: Record<ControlCheck["status"], string> = {
  ok: "Conforme",
  warn: "À vérifier",
  fail: "Non conforme",
  na: "Sans objet",
};

const DOC_LABEL: Record<string, string> = {
  detaillee: "Facture détaillée",
  normale: "Facture normale",
  synthetique: "Facture synthétique",
};

function money(value: number | null, currency: string): string {
  if (value === null) return "—";
  return `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function period(start: string | null, end: string | null): string {
  if (!start && !end) return "—";
  return `${start ?? "?"} → ${end ?? "?"}`;
}

/**
 * Compte Sinistre : contre-vérification des factures codées par l'Audit, avant leur validation finale
 * (montants, actes pris en compte, données du portefeuille de l'assureur). Une anomalie renvoie la facture à l'Audit.
 */
export function ClaimsControlPage() {
  const { showToast } = useToast();
  const [tab, setTab] = useState<ClaimsStatus>("pending");
  const [queue, setQueue] = useState<ControlQueueItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ControlDetail | null>(null);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadQueue = useCallback(async (status: ClaimsStatus) => {
    try {
      setError(null);
      const rows = await fetchControlQueue(status);
      setQueue(rows);
      setSelectedId(rows[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
      setQueue([]);
      setSelectedId(null);
    }
  }, []);

  useEffect(() => {
    void loadQueue(tab);
  }, [tab, loadQueue]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    fetchControlDetail(selectedId)
      .then((row) => {
        if (cancelled) return;
        setDetail(row);
        setNotes(row.invoice.claims_notes ?? "");
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : "Facture introuvable."));
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  // Après la validation finale de l'Audit, la décision Sinistre est figée.
  const closed = detail ? ["Approved", "Rejected", "Paid"].includes(detail.invoice.invoice_status) : false;

  const handleDecision = async (decision: "controlled" | "anomaly") => {
    if (!detail) return;
    if (decision === "anomaly" && !notes.trim()) {
      showToast({ message: "Décrivez l'anomalie constatée.", variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      await decideControl(detail.invoice.id, decision, notes.trim());
      showToast(decision === "controlled" ? "Facture validée." : "Anomalie enregistrée : la facture revient chez le médecin.");
      await loadQueue(tab);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const currency = detail?.invoice.currency ?? "USD";

  return (
    <>
      <Breadcrumbs items={[{ label: "Factures en attente" }]} />

      <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Statut du contrôle">
        {TABS.map((item) => (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={tab === item.value}
            onClick={() => setTab(item.value)}
            className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
              tab === item.value ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted hover:bg-accent/20 hover:border-accent"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <section className="theme-card p-4 xl:col-span-2">
          <h1 className="mb-3 text-lg font-semibold text-foreground">Factures transmises par l'Audit</h1>
          {queue.length === 0 ? (
            <p className="text-sm text-muted">Aucune facture dans cette file.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {queue.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      item.id === selectedId ? "border-primary bg-primary/5" : "border-border hover:bg-accent/20 hover:border-accent"
                    }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-medium text-foreground">Facture #{item.id}</span>
                      <span className="tabular-nums text-foreground">{money(item.total_amount, item.currency)}</span>
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {item.hospital} · {item.company} · {period(item.period_start, item.period_end)}
                    </span>
                    {item.deadline ? (
                      <span className="mt-1 block">
                        <DeadlineBadge deadline={item.deadline} />
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="theme-card p-5 xl:col-span-3">
          {!detail ? (
            <p className="text-sm text-muted">Choisissez une facture.</p>
          ) : (
            <div className="flex flex-col gap-6">
              <div>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="text-lg font-semibold text-foreground">Facture #{detail.invoice.id}</h2>
                  <DeadlineBadge deadline={detail.invoice.deadline} />
                </div>
                <p className="text-sm text-muted">
                  {detail.invoice.hospital} · {detail.invoice.company} · {period(detail.invoice.period_start, detail.invoice.period_end)}
                </p>
              </div>

              <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: "Facturé (détaillée)", value: money(detail.invoice.total_amount, currency) },
                  { label: "Somme des actes", value: money(detail.amounts.billed, currency) },
                  { label: "Réfacté par l'Audit", value: money(detail.amounts.refracted, currency) },
                  { label: "Pris en charge (package)", value: money(detail.amounts.covered, currency) },
                  { label: "Montant validé", value: money(detail.invoice.approved_amount, currency) },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border border-border p-3">
                    <dt className="text-xs text-muted">{item.label}</dt>
                    <dd className="mt-1 text-sm font-semibold tabular-nums text-foreground">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">Contrôles automatiques</h3>
                <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
                  {detail.checks.map((check) => (
                    <li key={check.key} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0">
                        <span className="block font-medium text-foreground">{check.label}</span>
                        <span className="block text-xs text-muted">{check.message}</span>
                      </span>
                      <StatusBadge variant={CHECK_VARIANT[check.status]}>{CHECK_LABEL[check.status]}</StatusBadge>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">Codification de l'Audit par patient</h3>
                <PatientCodingSummary patients={detail.coding} currency={currency} />
              </div>

              {detail.limit_issues.length > 0 ? (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-foreground">Plafonds et couverture</h3>
                  <ul className="flex flex-col gap-1 rounded-lg border border-border p-3 text-sm text-red-600">
                    {detail.limit_issues.map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {detail.price_lines.length > 0 ? (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-foreground">Lignes au-dessus de la grille</h3>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                        <tr>
                          <th className="px-3 py-2">Acte</th>
                          <th className="px-3 py-2 text-right">Prix facturé</th>
                          <th className="px-3 py-2 text-right">Prix grille</th>
                          <th className="px-3 py-2 text-right">Qté</th>
                          <th className="px-3 py-2 text-right">Dépassement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.price_lines.map((line, index) => (
                          <tr key={index} className="border-t border-border">
                            <td className="px-3 py-2 text-foreground">{line.act}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{money(line.billed_unit_price, currency)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{money(line.grid_price, currency)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{line.quantity}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-red-600">{money(line.excess, currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">Pièces reçues</h3>
                {detail.documents.length === 0 ? (
                  <p className="text-sm text-muted">Aucune pièce complémentaire enregistrée.</p>
                ) : (
                  <ul className="flex flex-col gap-1 text-sm">
                    {detail.documents.map((doc) => (
                      <li key={doc.id} className="flex justify-between gap-3">
                        <span className="text-foreground">{DOC_LABEL[doc.type] ?? doc.type}</span>
                        <span className="tabular-nums text-muted">{money(doc.declared_total, currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">Note de synthèse de l'Audit</h3>
                <p className="whitespace-pre-line rounded-lg bg-card-muted p-3 text-sm text-foreground">
                  {detail.invoice.audit_note || "Aucune note."}
                </p>
              </div>

              {closed && detail.invoice.invoice_status !== "Rejected" ? (
                <div className="rounded-lg border border-primary/20 bg-primary-soft/40 p-4">
                  <h3 className="text-sm font-semibold text-foreground">Note de paiement à l'assureur</h3>
                  <p className="mt-1 text-xs text-muted">
                    Montant à payer au prestataire, ventilé par entreprise.
                    {detail.invoice.payment_note_sent_at
                      ? ` Envoyée le ${new Date(detail.invoice.payment_note_sent_at.replace(" ", "T")).toLocaleDateString("fr-FR")}.`
                      : " Pas encore envoyée."}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void openPaymentNote(detail.invoice.id, "claims").catch((err) => setError(err instanceof Error ? err.message : "Note indisponible."))}
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-foreground hover:bg-accent/20 hover:border-accent"
                    >
                      Ouvrir la note de paiement
                    </button>
                    {!detail.invoice.payment_note_sent_at ? (
                      <button
                        type="button"
                        onClick={() =>
                          void markPaymentNoteSent(detail.invoice.id)
                            .then((sentAt) => {
                              setDetail({ ...detail, invoice: { ...detail.invoice, payment_note_sent_at: sentAt } });
                              showToast("Note de paiement marquée comme envoyée.");
                            })
                            .catch((err) => setError(err instanceof Error ? err.message : "Enregistrement impossible."))
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
                      >
                        <Check size={15} />
                        Marquer comme envoyée
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {closed ? (
                <p className="rounded-lg bg-card-muted p-3 text-sm text-muted">
                  Facture clôturée par l'Audit ({detail.invoice.invoice_status === "Rejected" ? "rejetée" : "validée"}) : le contrôle n'est plus modifiable.
                </p>
              ) : null}

              <FormField label="Observations du Sinistre" hint="Obligatoire pour signaler une anomalie : la facture revient à l'Audit.">
                <textarea
                  id="claims-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  className={`${textareaClass} min-h-[90px]`}
                  disabled={saving}
                />
              </FormField>

              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => void openRefactionSheet(detail.invoice.id).catch((err) => setError(err instanceof Error ? err.message : "Fiche indisponible."))}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-foreground transition-colors hover:bg-accent/20 hover:border-accent"
                >
                  Fiche de réfaction
                </button>
                <button
                  type="button"
                  disabled={saving || closed}
                  onClick={() => void handleDecision("anomaly")}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60"
                >
                  <TriangleAlert size={15} />
                  Signaler une anomalie
                </button>
                <button
                  type="button"
                  disabled={saving || closed}
                  onClick={() => void handleDecision("controlled")}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                >
                  <Check size={15} />
                  Valider la facture
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
